// src/pages/Onboarding/components/PatientFileDeclaration.js
import React, { useEffect, useState, useRef } from 'react';
import checkmark from '../../images/checkmarkIcon.svg';
import { ReactComponent as Gear } from '../../images/gear.svg';

export default function PatientFileDeclaration({
  dynamicText = 'your latest selection',
  theme = 'dark',
  durationMs = 1500,
  className = '',
  // NEW: controlled animation. When undefined, keep legacy auto-start behavior.
  // If provided, animation will start only when this value changes (boolean true or tick increment).
  animateNow,
  onDone,
}) {
  const [done, setDone] = useState(false);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef(null);
  const holdRef = useRef(null);
  const lastTriggerRef = useRef(null);
  // Store latest onDone without retriggering effects
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  // Split total duration into spin + checkmark hold
  const SPIN_MS = Math.max(220, Math.floor(durationMs * 0.35));
  const HOLD_MS = Math.max(260, durationMs - SPIN_MS);

  // Legacy auto-start behavior (animate on mount or when dynamicText/duration change)
  useEffect(() => {
    if (typeof animateNow !== 'undefined') return; // controlled mode elsewhere

    setDone(false);
    setAnimating(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (holdRef.current) clearTimeout(holdRef.current);
    timerRef.current = setTimeout(() => {
      setDone(true);
      setAnimating(false);
      // Hold the checkmark before notifying parent
      holdRef.current = setTimeout(() => { onDoneRef.current && onDoneRef.current(); }, HOLD_MS);
    }, SPIN_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (holdRef.current) clearTimeout(holdRef.current);
    };
  }, [dynamicText, SPIN_MS, HOLD_MS, animateNow]);

  // Controlled mode: only animate when animateNow changes to a positive value
  useEffect(() => {
    if (typeof animateNow === 'undefined') return; // legacy handled above

    // Ignore initial zero or non-positive values; require a positive tick
    if (typeof animateNow !== 'number' || animateNow <= 0) return;

    if (lastTriggerRef.current === animateNow) return;
    lastTriggerRef.current = animateNow;

    if (timerRef.current) clearTimeout(timerRef.current);
    if (holdRef.current) clearTimeout(holdRef.current);
    setDone(false);
    setAnimating(true);
    timerRef.current = setTimeout(() => {
      setDone(true);
      setAnimating(false);
      holdRef.current = setTimeout(() => { onDoneRef.current && onDoneRef.current(); }, HOLD_MS);
    }, SPIN_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (holdRef.current) clearTimeout(holdRef.current);
    };
  }, [animateNow, SPIN_MS, HOLD_MS]);

  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-gray-200' : 'text-gray-800';
  const cardBg = isDark ? 'bg-gray-900/40' : 'bg-gray-50';
  const iconCommon = 'absolute inset-0 w-6 h-6 transition-opacity duration-300';

  // Use static phrasing regardless of animation state
  const verb = 'will be updated';

  return (
    <div
      className={`rounded-xl ${cardBg} p-3 ${className}`}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className={`flex items-center gap-3 ${textColor}`}>
        {/* Icon stack: spinning gear → checkmark */}
        <div className="relative w-6 h-6" aria-hidden="true">
          {/* Gear: show static when not animating; spin only during active animation */}
          <Gear className={`${iconCommon} ${!done ? (animating ? 'opacity-100 animate-spin motion-reduce:animate-none' : 'opacity-100') : 'opacity-0'}`} />


          {/* Checkmark */}
          <img
            src={checkmark}
            alt=""
            className={`${iconCommon} ${done ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>

        <p className="text-xs leading-relaxed">
          <span className="font-medium">Your Patient File™</span> {verb} with{' '}
          <span className="font-semibold">{dynamicText}</span>.
        </p>
      </div>
    </div>
  );
}
